export const JAPANESE_PLATE_REGIONS = [
  '札幌',
  '函館',
  '旭川',
  '室蘭',
  '釧路',
  '帯広',
  '北見',
  '苫小牧',
  '知床',
  '十勝',
  '青森',
  '弘前',
  '八戸',
  '岩手',
  '盛岡',
  '平泉',
  '宮城',
  '仙台',
  '秋田',
  '山形',
  '庄内',
  '福島',
  '会津',
  '郡山',
  '白河',
  'いわき',
  '水戸',
  '土浦',
  'つくば',
  '宇都宮',
  '那須',
  '佐野',
  'とちぎ',
  '日光',
  '群馬',
  '高崎',
  '前橋',
  '大宮',
  '川口',
  '所沢',
  '川越',
  '熊谷',
  '春日部',
  '越谷',
  '千葉',
  '成田',
  '習志野',
  '市川',
  '船橋',
  '袖ケ浦',
  '市原',
  '野田',
  '柏',
  '松戸',
  '品川',
  '世田谷',
  '練馬',
  '杉並',
  '板橋',
  '足立',
  '江東',
  '葛飾',
  '江戸川',
  '八王子',
  '多摩',
  '横浜',
  '川崎',
  '相模',
  '湘南',
  '山梨',
  '富士山',
  '新潟',
  '長岡',
  '上越',
  '富山',
  '石川',
  '金沢',
  '長野',
  '松本',
  '諏訪',
  '安曇野',
  '南信州',
  '福井',
  '岐阜',
  '飛騨',
  '静岡',
  '浜松',
  '沼津',
  '伊豆',
  '名古屋',
  '豊橋',
  '三河',
  '岡崎',
  '豊田',
  '一宮',
  '春日井',
  '尾張小牧',
  '三重',
  '鈴鹿',
  '四日市',
  '伊勢志摩',
  '滋賀',
  '京都',
  '大阪',
  'なにわ',
  '和泉',
  '堺',
  '奈良',
  '飛鳥',
  '和歌山',
  '神戸',
  '姫路',
  '鳥取',
  '島根',
  '出雲',
  '岡山',
  '倉敷',
  '広島',
  '福山',
  '山口',
  '下関',
  '徳島',
  '香川',
  '高松',
  '愛媛',
  '高知',
  '福岡',
  '北九州',
  '久留米',
  '筑豊',
  '佐賀',
  '長崎',
  '佐世保',
  '熊本',
  '大分',
  '宮崎',
  '鹿児島',
  '奄美',
  '沖縄',
] as const;

const REGIONS_BY_LENGTH = [...JAPANESE_PLATE_REGIONS].sort((left, right) => {
  return right.length - left.length || left.localeCompare(right, 'ja');
});

export interface PlateParts {
  region: string;
  classNo: string;
  kana: string;
  serialDigits: string;
}

export const emptyPlateParts: PlateParts = {
  region: '',
  classNo: '',
  kana: '',
  serialDigits: '',
};

export const normalizeClassNo = (value: string): string =>
  value
    .normalize('NFKC')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 3);

export const normalizeKana = (value: string): string =>
  value.normalize('NFKC').match(/[ぁ-ん]/u)?.[0] ?? '';

const normalizePlateText = (value: string): string =>
  value
    .normalize('NFKC')
    .replace(/[‐‑‒–—―−ー]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

const serialDigitsFrom = (value: string): string =>
  value.normalize('NFKC').replace(/\D/g, '').slice(-4);

export const formatSerial = (digits: string): string => {
  if (!digits) return '';
  if (digits.length >= 4) return digits.slice(0, 4);
  return `${'・'.repeat(4 - digits.length)}${digits}`;
};

const parseCompactPlate = (text: string): PlateParts | null => {
  for (const region of REGIONS_BY_LENGTH) {
    if (!text.startsWith(region)) continue;
    const tail = text.slice(region.length);
    const match = tail.match(/^([0-9A-Z]{1,3})([ぁ-ん])(.+)$/u);
    if (!match) return null;
    return {
      region,
      classNo: normalizeClassNo(match[1] ?? ''),
      kana: normalizeKana(match[2] ?? ''),
      serialDigits: serialDigitsFrom(match[3] ?? ''),
    };
  }

  const fallback = text.match(/^(.+?)([0-9A-Z]{1,3})([ぁ-ん])(.+)$/u);
  if (!fallback) return null;
  return {
    region: fallback[1] ?? '',
    classNo: normalizeClassNo(fallback[2] ?? ''),
    kana: normalizeKana(fallback[3] ?? ''),
    serialDigits: serialDigitsFrom(fallback[4] ?? ''),
  };
};

export const parseJapaneseVehiclePlate = (value?: string | null): PlateParts => {
  const text = normalizePlateText(value ?? '');
  if (!text) return emptyPlateParts;
  const tokens = text.split(/\s+/);
  if (tokens.length >= 4) {
    return {
      region: tokens[0] ?? '',
      classNo: normalizeClassNo(tokens[1] ?? ''),
      kana: normalizeKana(tokens[2] ?? ''),
      serialDigits: serialDigitsFrom(tokens.slice(3).join('')),
    };
  }
  if (tokens.length === 1) {
    const compactParts = parseCompactPlate(text);
    if (compactParts) return compactParts;
  }
  return {
    region: tokens[0] ?? '',
    classNo: normalizeClassNo(tokens[1] ?? ''),
    kana: normalizeKana(tokens[2] ?? ''),
    serialDigits: serialDigitsFrom(tokens[3] ?? ''),
  };
};

export const composeJapaneseVehiclePlate = (parts: PlateParts): string => {
  const serial = formatSerial(parts.serialDigits);
  return [parts.region.trim(), parts.classNo.trim(), parts.kana.trim(), serial]
    .filter(Boolean)
    .join(' ');
};
